import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
  handleSendOtp,
  handleVerifyOtpRequest,
  showSuccess,
  throwError,
} from 'store/slices';
import {
  generatePreSignedUrl,
  getFormDataArrayObject,
  getUserType,
  objectToFormData,
  getStatus,
  getStatusColor,
  handleFileDownload,
} from 'utils/helpers';
import { icons, requestDataPath } from 'utils/constants';

import Button from 'components/form/Button';
import Card from 'components/Layout/Card';
import TextArea from 'components/form/TextArea';
import FileUpload from 'components/form/FileUpload';
import CheckBox from 'components/form/CheckBox';
import Loader from 'components/Layout/Loader';
import VerifyOtpModel from 'pages/Common/RequestRaisedAndReceived/Common/VerifyOtpModel';
import RequestVerificationModal from 'pages/Common/RequestRaisedAndReceived/Common/RequestVerificationModal';

const InfoRow = ({ label, value }) => (
  <div className="text-14-300-18 cmt-5">{value}</div>
);

const ReviewDetails = ({
  reviewerType = 'Reviewer',
  title,
  name,
  email,
  id,
  submitted,
  comment,
  status = 'Approved',
  fileName = '-',
}) => (
  <>
    <div className="cmt-35 text-14-500-18 color-new-car">{title}</div>
    <div className="cmt-24">
      <div className="row mb-2 gap-3">
        <div className="col-md-2 col-12 text-14-400-18">
          {reviewerType}{' '}
          <span>
            <InfoRow value={name} />
          </span>
        </div>
        <div className="col-md-2 col-12 text-14-400-18">
          Id{' '}
          <span>
            <InfoRow value={id} />
          </span>
        </div>
        <div className="col-md-2 col-12 text-14-400-18">
          Email Id{' '}
          <span>
            <InfoRow value={email} />
          </span>
        </div>
        <div className="col-md-2 col-12 text-14-400-18">
          Submitted{' '}
          <span>
            <InfoRow value={submitted} />
          </span>
        </div>
      </div>

      <div className="row mb-2 cmt-24">
        <div className="col-md-2 text-14-400-18">Submitted Documents</div>
        <div className="col-md-2 text-14-400-18">Status</div>
      </div>
      <div className="row mb-2">
        <div className="col-md-2 text-14-400-18">
          {fileName !== '-' ? (
            <a
              href="#"
              style={{
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
              onClick={(e) => handleFileDownload(e, fileName, requestDataPath)}
            >
              <img
                src={icons.ArrowDownward}
                alt=""
                width="20"
                height="20"
                style={{ marginRight: '6px' }}
              />
              {fileName}
            </a>
          ) : (
            '-'
          )}
        </div>
        <div className={`col-md-2 text-14-300-18 ${getStatusColor(status)}`}>
          {getStatus(status, 'request')}
        </div>
      </div>

      <div className="row mb-2 cmt-24">
        <div className="col-md-12 text-14-400-18">Comment</div>
      </div>
      <div className="row mb-2">
        <div className="col-md-12 text-14-300-18">
          {comment ? comment : '-'}
        </div>
      </div>
    </div>
  </>
);

const EventInfoSection = ({ eventName, addedBy, id, email, submittedDate }) => (
  <>
    <div className="cmb-24">
      <div className="text-14-400-18 cmb-8">Event Name</div>
      <div className="text-14-300-18">{eventName}</div>
    </div>

    <div className="row mb-2 gap-3">
      <div className="col-md-2 col-12 text-14-400-18">
        Added By{' '}
        <span>
          <InfoRow value={addedBy} />
        </span>
      </div>
      <div className="col-md-2 col-12 text-14-400-18">
        Id{' '}
        <span>
          <InfoRow value={id} />
        </span>
      </div>
      <div className="col-md-2 col-12 text-14-400-18">
        Email Id{' '}
        <span>
          <InfoRow value={email} />
        </span>
      </div>
      <div className="col-md-2 col-12 text-14-400-18">
        Submitted{' '}
        <span>
          <InfoRow value={submittedDate} />
        </span>
      </div>
    </div>
  </>
);

const EventReviewTrack = () => {
  const { userDetails, requestEventData, isReviewEvent } = useSelector(
    (state) => ({
      userDetails: state.student.userDetails,
      requestEventData: state.global.requestEventData,
      isReviewEvent: state.global.isReviewEvent,
    })
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userType = getUserType();

  const { user_type } = userDetails || {};
  const params = useParams();

  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState(false);

  const [isRejecting, setIsRejecting] = useState(false);
  const [otpData, setOtpData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [otpVerification, setOtpVerification] = useState(false);
  // Memoized data calculations
  const { requestDetails, eventData, adminReviewData, superAdminReviewData } =
    useMemo(() => {
      const requestDetails = requestEventData?.reqeust_basic_details || {};
      const {
        admin = {},
        resource = {},
        sadmin = {},
        event_name = '',
      } = requestDetails;

      return {
        requestDetails,
        eventData: {
          name: event_name,
          addedBy: resource.added_by || '',
          id: resource.member_id || '',
          email: resource.email_id || '',
          submittedDate: resource.request_date || '',
        },
        adminReviewData: {
          reviewerType: 'Admin',
          title: 'Admin Review Details :',
          name: admin?.admin_name || '',
          email: admin.admin_email_id || '',
          id: admin.admin_member_id || '',
          submitted: admin.admin_submited_at || '',
          comment: admin.admin_comment || '',
          status: admin.admin_status || '',
          fileName: admin.admin_document || '-',
        },
        superAdminReviewData: {
          reviewerType: 'Super Admin',
          title: 'Super Admin Review Details :',
          name: sadmin.sadmin_name || '',
          email: sadmin.sadmin_email_id || '',
          id: sadmin.sadmin_member_id || '',
          submitted: sadmin.sadmin_submited_at || '',
          comment: sadmin.sadmin_comment || '',
          status: sadmin.sadmin_status || '',
          fileName: sadmin.sadmin_document || '-',
        },
      };
    }, [requestEventData]);

  const isAdminReviewAvailable = adminReviewData.name || adminReviewData.email;
  const isSuperAdminReviewAvailable =
    superAdminReviewData.name || superAdminReviewData.email;

  const initialValues = {
    comment: '',
    document: '',
    confirmCheck:
      requestEventData?.is_visible_to_third_party === '1' ? true : false,
  };

  const validationSchema = Yup.object().shape({
    confirmCheck: Yup.string(),
    comment: Yup.string(),
    document: Yup.mixed(),
  });

  const handleReject = async (values, setErrors) => {
    setIsRejecting(true);

    if (!values.comment.trim() && !values.document) {
      setErrors({
        comment: 'Either a comment or a document is required',
        document: 'Either a comment or a document is required',
      });
      return;
    }

    setOtpData({
      comment: values?.comment,
      document: values?.document,
      action: '2', // Reject Status
      is_check: values?.confirmCheck ? '1' : '0',
    });

    await sendOtpRequest();
  };

  const handleApproveRequest = async (values, setErrors) => {
    setOtpData({
      comment: values?.comment,
      document: values?.document,
      action: '1', // Approve
      is_check: values?.confirmCheck ? '1' : '0',
    });
    if (user_type === '1') {
      await sendOtpRequest();
    } else {
      setIsOpenConfirmationDialog(true);
    }
  };

  const sendOtpRequest = async () => {
    setIsLoading(true);
    const payload = objectToFormData({
      request_id: params?.eventId,
      action: otpData?.action
    });

    try {
      const response = await dispatch(handleSendOtp(payload));
      if (response.status === 200) {
        setShowOtpDialog(true);
        dispatch(showSuccess(response.message));
      }
    } catch (error) {
      dispatch(throwError({ message: 'Failed to process request' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setOtpVerification(true);
    const payload = getFormDataArrayObject({
      request_id: params?.eventId,
      ...(otp && { otp }),
      document: otpData?.document,
      comment: otpData?.comment,
      request_status: otpData?.action,
      request_data: 1,
      is_visible_to_third_party: otpData?.is_check,
    });

    try {
      const response = await dispatch(handleVerifyOtpRequest(payload));
      if (response.status === 200) {
        navigate(`/${userType}/request-received/event-management`);
        setShowOtpDialog(false);
        dispatch(showSuccess(response.message));
      }
    } catch (error) {
      console.error('Verification failed:', error);
    } finally {
      setOtpVerification(false);
    }
  };

  const renderActionButtons = (props, isAdmin = false) => {
    const { values, setErrors } = props;
    return (
      <>
        <div className="d-flex gap-3 cpt-19">
          <Button
            text={user_type === '1' ? 'Approve Request' : 'Send Request'}
            btnStyle="primary-dark"
            className="h-37 text-14-500 cps-20 cpe-20"
            onClick={() => handleApproveRequest(values, setErrors)}
            disabled={isLoading}
          />
          <Button
            text="Reject"
            btnStyle="primary-light-outline"
            className="h-37 text-14-500 cps-20 cpe-20"
            onClick={() => handleReject(values, setErrors)}
            disabled={isLoading}
          />
          <Button
            text="Cancel"
            btnStyle="light-outline"
            className="h-37 text-14-500 cps-20 cpe-20"
            disabled={isLoading}
            onClick={() => navigate(-1)}
          />
        </div>
        {isAdmin && (
          <div className="d-flex align-items-center gap-2 cmt-17">
            <i className="bi bi-info-circle"></i>
            <span>
              This send request will be considered as an approval from the admin
              and will be shared with the super admin.
            </span>
          </div>
        )}
      </>
    );
  };

  const renderConfirmationCheckbox = (props) => {
    const { values, setFieldValue, errors } = props;
    return (
      <>
        <div className="d-flex align-items-center gap-2 cmt-17">
          <CheckBox
            type="PRIMARY-ACTIVE"
            onClick={() => setFieldValue('confirmCheck', !values.confirmCheck)}
            isChecked={values.confirmCheck}
          />
          <span>
            I confirm this information should be reflected on both the event
            website and the IFERP Dashboard.
          </span>
        </div>
      </>
    );
  };

  return (
    <>
      {isReviewEvent ? (
        <Card className="cps-20 cpt-20 cpb-20 cpe-20">
          <Loader size="sm" />
        </Card>
      ) : (
        <Card>
          <div className="d-flex justify-content-between align-items-center cmb-14">
            <h2 className="text-20-400-25 cps-24 cpt-24">Review the Event :</h2>
          </div>
          <hr />

          <div className="cps-24 cpb-10">
            <EventInfoSection {...eventData} />

            {user_type === '6' && (
              <>
                {isAdminReviewAvailable && (
                  <ReviewDetails {...adminReviewData} />
                )}
                {isSuperAdminReviewAvailable && (
                  <ReviewDetails {...superAdminReviewData} />
                )}

                <div className="d-flex justify-content-start cmt-24">
                  <Button
                    text="Back"
                    btnStyle="light-outline"
                    className="cps-20 cpe-20 h-37"
                    onClick={() => navigate(-1)}
                  />
                </div>

                <div className="d-flex align-items-center gap-2 cmt-17">
                  <CheckBox
                    type="PRIMARY-ACTIVE"
                    isChecked={
                      requestEventData?.is_visible_to_third_party === '1'
                        ? true
                        : false
                    }
                    disabled
                  />
                  <span>
                    I confirm this information should be reflected on both the
                    event website and the IFERP Dashboard.
                  </span>
                </div>
              </>
            )}

            {user_type === '0' && (
              <>
                {adminReviewData.status !== '0' && adminReviewData?.submitted !== '' ? (
                  <>
                    <ReviewDetails {...adminReviewData} />
                    {isSuperAdminReviewAvailable && (
                      <ReviewDetails {...superAdminReviewData} />
                    )}
                  </>
                ) : (
                  <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, { setErrors }) => {
                      if (isRejecting) {
                        handleReject(values, setErrors);
                      } else {
                        handleApproveRequest(values, setErrors);
                      }
                    }}
                  >
                    {(props) => {
                      const { values, errors, handleChange, setFieldValue } =
                        props;

                      return (
                        <form>
                          <div className="row d-flex justify-content-between align-items-center cmb-26">
                            <div className="col-md-8 cmb-24">
                              <div className="cmt-10">
                                <TextArea
                                  rows={3}
                                  label="Comment"
                                  name="comment"
                                  value={values.comment}
                                  onChange={handleChange}
                                // error={errors.comment}
                                />
                              </div>
                              <div className="cmt-10">
                                <FileUpload
                                  label="Upload Supporting Document"
                                  id="document"
                                  value={values.document}
                                  onChange={(e) => {
                                    const id = e.target.id;
                                    const value = e.target.value;
                                    setFieldValue(id, value);
                                  }}
                                  fileType="image,pdf"
                                // error={errors.document}
                                />
                              </div>
                              {(errors.document || errors.comment) && (
                                <div className="text-12-400-15 color-4646 cmt-10">
                                  * Either comment or upload media file is
                                  mandatory
                                </div>
                              )}
                              {renderActionButtons(props, user_type === '0')}
                              {renderConfirmationCheckbox(props)}
                            </div>
                          </div>
                        </form>
                      );
                    }}
                  </Formik>
                )}
              </>
            )}

            {user_type === '1' && (
              <>
                {user_type === '1' && isAdminReviewAvailable && (
                  <ReviewDetails {...adminReviewData} />
                )}

                {superAdminReviewData.status !== '0' &&
                  superAdminReviewData?.submitted !== '' ? (
                  <ReviewDetails {...superAdminReviewData} />
                ) : (
                  <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, { setErrors }) => {
                      if (isRejecting) {
                        handleReject(values, setErrors);
                      } else {
                        handleApproveRequest(values, setErrors);
                      }
                    }}
                  >
                    {(props) => {
                      const { values, errors, handleChange, setFieldValue } =
                        props;

                      return (
                        <form>
                          <div className="row d-flex justify-content-between align-items-center cmb-26">
                            <div className="col-md-8 cmb-24">
                              <div className="cmt-10">
                                <TextArea
                                  rows={3}
                                  label="Comment"
                                  name="comment"
                                  value={values.comment}
                                  onChange={handleChange}
                                // error={errors.comment}
                                />
                              </div>
                              <div className="cmt-10">
                                <FileUpload
                                  label="Upload Supporting Document"
                                  id="document"
                                  value={values.document}
                                  onChange={(e) => {
                                    const id = e.target.id;
                                    const value = e.target.value;
                                    setFieldValue(id, value);
                                  }}
                                  fileType="image,pdf"
                                // error={errors.document}
                                />
                              </div>
                              {(errors.document || errors.comment) && (
                                <div className="text-12-400-15 color-4646 cmt-10">
                                  * Either comment or upload media file is
                                  mandatory
                                </div>
                              )}
                              {renderActionButtons(props, user_type === '0')}
                              {renderConfirmationCheckbox(props)}
                            </div>
                          </div>
                        </form>
                      );
                    }}
                  </Formik>
                )}
              </>
            )}
          </div>

          {showOtpDialog && (
            <VerifyOtpModel
              title="Event Request"
              showApproveDialog={showOtpDialog}
              onHide={() => setShowOtpDialog(false)}
              onVerify={handleVerifyOtp}
              sendOtpRequest={sendOtpRequest}
              otpVerification={otpVerification}
            />
          )}

          {isOpenConfirmationDialog && (
            <RequestVerificationModal
              handleConfirmation={() => handleVerifyOtp()}
              onHide={() => setIsOpenConfirmationDialog(false)}
              title="You want to share the approve request to super admin"
              isLoading={otpVerification}
            />
          )}
        </Card>
      )}
    </>
  );
};

export default EventReviewTrack;

// import React from 'react';
// import Button from 'components/form/Button';
// import Card from 'components/Layout/Card';
// import { useDispatch, useSelector } from 'react-redux';
// import { Formik } from 'formik';
// import * as Yup from "yup";
// import { getFilenameFromUrl, trimLeftSpace } from 'utils/helpers';
// import TextArea from 'components/form/TextArea';
// import { throwError } from 'store/slices';
// import FileUpload from 'components/form/FileUpload';
// import CheckBox from 'components/form/CheckBox';

// const InfoRow = ({ label, value }) => (
//   <div className="col-md-2 text-14-300-18">{value}</div>
// );

// const ReviewDetails = ({
//   reviewerType = 'Reviewer',
//   title,
//   name,
//   email,
//   id,
//   submitted,
//   comment,
//   status = 'Approved',
//   fileName = 'file.pdf',
// }) => (
//   <>
//     <div className="cmt-45 text-14-500-18 color-new-car">{title}</div>
//     <div className="cmt-24">
//       <div className="row mb-2">
//         <div className="col-md-2 text-14-400-18">{reviewerType}</div>
//         <div className="col-md-2 text-14-400-18">Id</div>
//         <div className="col-md-2 text-14-400-18">Email Id</div>
//         <div className="col-md-2 text-14-400-18">Submitted</div>
//       </div>
//       <div className="row mb-2">
//         <InfoRow value={name} />
//         <InfoRow value={id} />
//         <InfoRow value={email} />
//         <InfoRow value={submitted} />
//       </div>

//       <div className="row mb-2 cmt-24">
//         <div className="col-md-2 text-14-400-18">Submitted Documents</div>
//         <div className="col-md-2 text-14-400-18">Status</div>
//       </div>
//       <div className="row mb-2">
//         <div className="col-md-2 text-14-400-18">
//           <a href="#" style={{ textDecoration: 'none' }}>
//             {fileName}
//           </a>
//         </div>
//         <div className="col-md-2 text-success text-14-300-18">{status}</div>
//       </div>

//       <div className="row mb-2 cmt-24">
//         <div className="col-md-12 text-14-400-18">Comment</div>
//       </div>
//       <div className="row mb-2">
//         <div className="col-md-12 text-14-300-18">{comment}</div>
//       </div>
//     </div>
//   </>
// );

// const EventReviewTrack = () => {
//   const { userDetails } = useSelector((state) => ({
//     userDetails: state.student.userDetails,
//   }));
//   const dispatch = useDispatch();
//   const { user_type } = userDetails;

//   const initialValues = {
//     comment: '',
//     document: '',
//   };

//   const validationSchema = Yup.object().shape({
//     comment: Yup.string().required('Comment is required'),
//     document: Yup.mixed().required('Document is required'),
//   });  return (
//     <Card>
//       <div className="d-flex justify-content-between align-items-center cmb-14">
//         <h2 className="text-20-400-25 cps-24 cpt-24">Review the Event :</h2>
//       </div>
//       <hr />

//       {user_type === '6' && (
//         <div className="cps-24">
//           {/* Event Info */}

//           <div className="cmb-24">
//             <div className="text-14-400-18 cmb-8">Event Name</div>
//             <div className="text-14-300-18">
//               International Academic Conference on Teaching Education and Recent
//               Learning Technologies (ICTERLT)
//             </div>
//           </div>

//           <div className="row mb-2">
//             <div className="col-md-2 text-14-400-18">Added By</div>
//             <div className="col-md-2 text-14-400-18">Id</div>
//             <div className="col-md-2 text-14-400-18">Email Id</div>
//             <div className="col-md-2 text-14-400-18">Submitted</div>
//           </div>
//           <div className="row">
//             <InfoRow value="Santhosh" />
//             <InfoRow value="OR-0006" />
//             <InfoRow value="santhosh@technoare.org" />
//             <InfoRow value="10-June-2025" />
//           </div>

//           {/* Admin Review */}
//           <ReviewDetails
//             reviewerType="Admin"
//             title="Admin Review Details :"
//             name="Karthik Ethiraj"
//             email="Karthikehiraj@technoare.org"
//             id="ADMIN-0006"
//             submitted="10-June-2025"
//             comment="Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum"
//           />

//           {/* Super Admin Review */}
//           <ReviewDetails
//             reviewerType="Super Admin"
//             title="Super Admin Review Details :"
//             name="AR"
//             email="d@technoare.org"
//             id="ADMIN-0006"
//             submitted="10-June-2025"
//             comment="Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum"
//           />

//           {/* Back Button */}
//           <div className="d-flex justify-content-start cmt-24">
//             <Button
//               text="Back"
//               btnStyle="light-outline"
//               className="cps-20 cpe-20 h-37"
//             />
//           </div>

//           {/* Checkbox */}
//           <div className="form-check cmt-26 cmb-10">
//             <input
//               className="form-check-input"
//               type="checkbox"
//               id="confirmCheckbox"
//               defaultChecked
//             />
//             <label className="form-check-label" htmlFor="confirmCheckbox">
//               I confirm this information should be reflected on both the event
//               website and the IFERP Dashboard.
//             </label>
//           </div>
//         </div>
//       )}

//       {user_type === '0' && (
//         <>
//           <div className="cps-24">
//             <div className="cmb-24">
//               <div className="text-14-400-18 cmb-8">Event Name</div>
//               <div className="text-14-300-18">
//                 International Academic Conference on Teaching Education and
//                 Recent Learning Technologies (ICTERLT)
//               </div>
//             </div>

//             <div className="row mb-2">
//               <div className="col-md-2 text-14-400-18">Added By</div>
//               <div className="col-md-2 text-14-400-18">Id</div>
//               <div className="col-md-2 text-14-400-18">Email Id</div>
//               <div className="col-md-2 text-14-400-18">Submitted</div>
//             </div>
//             <div className="row">
//               <InfoRow value="Santhosh" />
//               <InfoRow value="OR-0006" />
//               <InfoRow value="santhosh@technoare.org" />
//               <InfoRow value="10-June-2025" />
//             </div>

//             <div>
//               <Formik
//                 enableReinitialize
//                 initialValues={initialValues}
//                 // onSubmit={handelSave}
//                 validationSchema={validationSchema}
//               >
//                 {(props) => {
//                   const {
//                     values,
//                     errors,
//                     handleChange,
//                     handleSubmit,
//                     resetForm,
//                     setFieldValue,
//                   } = props;
//                   return (
//                     <form>
//                       <div className="row d-flex justify-conent-between align-items-center cmb-26">
//                         <div className="col-md-8 cmb-24">
//                           <div className="cmt-10">
//                             <TextArea
//                               rows={3}
//                               label="Comment"
//                               placeholder="Enter comment"
//                               value={values.comment}
//                               error={errors.comment}
//                               id="comment"
//                               onChange={(e) =>
//                                 handleChange({
//                                   target: {
//                                     id: 'comment',
//                                     value: trimLeftSpace(e.target.value),
//                                   },
//                                 })
//                               }
//                               // disabled={isViewMode}
//                             />
//                           </div>
//                           <div className="cmt-10">
//                             <FileUpload
//                               label="Document"
//                               fileType="image"
//                               fileText={getFilenameFromUrl(
//                                 values.documentFileName
//                               )}
//                               id="document"
//                               onChange={(e) => {
//                                 const value = e.target.value;
//                                 const fileName = e.target.fileName;
//                                 let fileType = fileName
//                                   ? fileName?.split('.')?.pop()
//                                   : '';
//                                 if (['png', 'jpg', 'jpeg'].includes(fileType)) {
//                                   setFieldValue('document', value);
//                                   setFieldValue('documentFileName', fileName);
//                                 } else {
//                                   setFieldValue('documentFileName', '');
//                                   dispatch(
//                                     throwError({
//                                       message: 'Invalid file selected',
//                                     })
//                                   );
//                                 }
//                               }}
//                               value={values.document}
//                               error={errors.document}
//                               // disabled={isViewMode}
//                             />
//                           </div>
//                           <div className="d-flex gap-3 cpt-19">
//                             <Button
//                               text="Send Request"
//                               btnStyle="primary-dark"
//                               className="h-37 text-14-500 cps-20 cpe-20"
//                             />
//                             <Button
//                               text="Reject"
//                               btnStyle="primary-light-outline"
//                               className="h-37 text-14-500 cps-20 cpe-20"
//                             />
//                             <Button
//                               text="Cancel"
//                               btnStyle="light-outline"
//                               className="h-37 text-14-500 cps-20 cpe-20"
//                             />
//                           </div>
//                           <div className="d-flex align-items-center gap-2 cmt-17">
//                             <i class="bi bi-info-circle"></i>
//                             <span>
//                               This send request will consider as approve from
//                               the admin & this approve request will be share to
//                               the super admin
//                             </span>
//                           </div>
//                           <div className="d-flex align-items-center gap-2 cmt-17">
//                             <CheckBox
//                               type="PRIMARY-ACTIVE"
//                               onClick={() => {}}
//                               isChecked={true}
//                             />
//                             <span>
//                               I confirm this information should be reflected on
//                               both the event website and the IFERP Dashboard.
//                             </span>
//                           </div>
//                         </div>
//                       </div>
//                     </form>
//                   );
//                 }}
//               </Formik>
//             </div>
//           </div>
//         </>
//       )}

//       {user_type === '1' && (
//         <>
//           <div className="cps-24">
//             <div className="cmb-24">
//               <div className="text-14-400-18 cmb-8">Event Name</div>
//               <div className="text-14-300-18">
//                 International Academic Conference on Teaching Education and
//                 Recent Learning Technologies (ICTERLT)
//               </div>
//             </div>

//             <div className="row mb-2">
//               <div className="col-md-2 text-14-400-18">Added By</div>
//               <div className="col-md-2 text-14-400-18">Id</div>
//               <div className="col-md-2 text-14-400-18">Email Id</div>
//               <div className="col-md-2 text-14-400-18">Submitted</div>
//             </div>
//             <div className="row">
//               <InfoRow value="Santhosh" />
//               <InfoRow value="OR-0006" />
//               <InfoRow value="santhosh@technoare.org" />
//               <InfoRow value="10-June-2025" />
//             </div>

//             {/* Admin Review */}
//             <ReviewDetails
//               reviewerType="Admin"
//               title="Admin Review Details :"
//               name="Karthik Ethiraj"
//               email="Karthikehiraj@technoare.org"
//               id="ADMIN-0006"
//               submitted="10-June-2025"
//               comment="Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum"
//             />

//             <div className="d-flex gap-3 cpt-19">
//               <Button
//                 text="Send Request"
//                 btnStyle="primary-dark"
//                 className="h-37 text-14-500 cps-20 cpe-20"
//               />
//               <Button
//                 text="Reject"
//                 btnStyle="primary-light-outline"
//                 className="h-37 text-14-500 cps-20 cpe-20"
//               />
//               <Button
//                 text="Cancel"
//                 btnStyle="light-outline"
//                 className="h-37 text-14-500 cps-20 cpe-20"
//               />
//             </div>

//             <div className="d-flex align-items-center gap-2 cmt-17">
//               <CheckBox
//                 type="PRIMARY-ACTIVE"
//                 onClick={() => {}}
//                 isChecked={true}
//               />
//               <span>
//                 I confirm this information should be reflected on both the event
//                 website and the IFERP Dashboard.
//               </span>
//             </div>
//           </div>
//         </>
//       )}
//     </Card>
//   );
// };

// export default EventReviewTrack;
